let audioCtx = null;
let unlocked = false;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try {
      fn(isOrderSoundUnlocked());
    } catch {
      /* ignore */
    }
  });
}

function getCtx() {
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
  }
  return audioCtx;
}

export function isOrderSoundUnlocked() {
  return unlocked && !!audioCtx && audioCtx.state === 'running';
}

/** Suscribirse a cambios (para banner “Activar sonido”). */
export function onSoundUnlockChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Debe llamarse desde un gesto del usuario (PIN, botón, toque).
 * En tablets Android el audio no suena hasta esto.
 */
export async function unlockNewOrderSound() {
  const ctx = getCtx();
  if (!ctx) {
    unlocked = true;
    notify();
    return false;
  }
  unlocked = true;
  try {
    if (ctx.state === 'suspended') await ctx.resume();
  } catch {
    /* ignore */
  }
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = 0.0001;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.05);
  } catch {
    /* ignore */
  }
  notify();
  return ctx.state === 'running';
}

function beepBurst(ctx, baseTime, vol = 0.55) {
  const tone = (freq, start, dur, v = vol) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = freq;
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, baseTime + start);
    gain.gain.exponentialRampToValueAtTime(v, baseTime + start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, baseTime + start + dur);
    osc.start(baseTime + start);
    osc.stop(baseTime + start + dur + 0.05);
  };
  tone(880, 0, 0.18, vol);
  tone(1175, 0.2, 0.18, vol);
  tone(1480, 0.4, 0.32, vol);
}

function flashTitle(message) {
  const original = document.title;
  let n = 0;
  const t = setInterval(() => {
    document.title = n % 2 === 0 ? `🔔 ${message}` : original;
    n += 1;
    if (n > 12) {
      clearInterval(t);
      document.title = original;
    }
  }, 600);
}

/** Genera un beep WAV corto en memoria (fallback Android). */
function playHtmlBeep() {
  try {
    const sampleRate = 22050;
    const duration = 0.22;
    const n = Math.floor(sampleRate * duration);
    const dataSize = n * 2;
    const buf = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buf);
    const writeStr = (offset, str) => {
      for (let i = 0; i < str.length; i += 1) view.setUint8(offset + i, str.charCodeAt(i));
    };
    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeStr(36, 'data');
    view.setUint32(40, dataSize, true);
    for (let i = 0; i < n; i += 1) {
      const t = i / sampleRate;
      const env = Math.min(1, i / 400) * Math.min(1, (n - i) / 800);
      const sample = Math.sin(2 * Math.PI * 980 * t) * 0.55 * env;
      view.setInt16(44 + i * 2, Math.max(-1, Math.min(1, sample)) * 0x7fff, true);
    }
    const url = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
    const a = new Audio(url);
    a.volume = 1;
    const p = a.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch {
    /* ignore */
  }
}

/**
 * Alarma cuando llega un pedido aprobado a bodega.
 * Espera a que el AudioContext esté running (crítico en tablets).
 */
export async function playNewOrderSound({ title = 'Pedido a bodega', short = false } = {}) {
  try {
    navigator.vibrate?.([250, 120, 250, 120, 400, 120, 250]);
  } catch {
    /* ignore */
  }

  if (!short) flashTitle(title);

  const ctx = getCtx();
  if (ctx) {
    try {
      if (ctx.state === 'suspended') await ctx.resume();
    } catch {
      /* ignore */
    }
  }

  if (!ctx || ctx.state !== 'running') {
    playHtmlBeep();
    if (!short) {
      setTimeout(playHtmlBeep, 700);
      setTimeout(playHtmlBeep, 1400);
      setTimeout(playHtmlBeep, 2100);
    }
    notify();
    return;
  }

  unlocked = true;
  notify();

  const t0 = ctx.currentTime;
  const times = short ? 1 : 5;
  for (let i = 0; i < times; i += 1) {
    beepBurst(ctx, t0 + i * 0.75, short ? 0.4 : 0.58);
  }
}
